import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { getDashboardChat, sendDashboardChat } from '../services/api';
import { 
  Send, ArrowLeft, MessageSquare, Loader2, Lock, Crown, 
  MapPin, Home, Euro
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatPage = () => {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const landlordId = searchParams.get('landlordId');
  const listingTitle = searchParams.get('title') || 'Woning';
  const landlordName = searchParams.get('landlord') || 'Verhuurder';
  
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const chatEndRef = useRef(null);
  
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(true);
  const [listing, setListing] = useState(null);

  // FREE FOR ALL - Everyone has full access to chat
  const hasPremium = true;

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Load listing details
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/listings/${listingId}`);
        if (response.ok) {
          const data = await response.json();
          setListing(data);
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
      }
    };
    
    if (listingId) {
      fetchListing();
    }
  }, [listingId]);

  // Load chat messages
  useEffect(() => {
    const loadChat = async () => {
      if (!listingId || !landlordId || !isAuthenticated) return;
      
      setLoadingChat(true);
      try {
        const response = await getDashboardChat(listingId, landlordId);
        setChatMessages(response.messages || []);
      } catch (error) {
        console.error('Error loading chat:', error);
      } finally {
        setLoadingChat(false);
      }
    };
    
    loadChat();
  }, [listingId, landlordId, isAuthenticated]);

  // Scroll to bottom when messages update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sendingMessage || !hasPremium) return;
    
    setSendingMessage(true);
    try {
      const res = await sendDashboardChat(null, listingId, landlordId, newMessage.trim());
      
      // Add message to local state
      setChatMessages(prev => [...prev, res.message || {
        id: Date.now().toString(),
        senderId: user.id,
        sender: 'me',
        content: newMessage.trim(),
        timestamp: new Date().toISOString()
      }]);
      
      setNewMessage('');
    } catch (error) {
      toast({
        title: language === 'nl' ? 'Fout' : 'Error',
        description: language === 'nl' ? 'Bericht kon niet worden verzonden' : 'Could not send message',
        variant: 'destructive'
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return language === 'nl' ? 'Nu' : 'Now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' });
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam' });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7] flex flex-col">
      <Toaster />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F3A5F] to-[#0D2137] text-white sticky top-0 z-50">
        <div className="flex items-center gap-3 p-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg truncate">
              {language === 'nl' ? 'Chat met' : 'Chat with'} {listing?.landlord?.name || landlordName}
            </h1>
            <p className="text-white/70 text-sm truncate">{listing?.title || listingTitle}</p>
          </div>
        </div>
        
        {/* Listing preview bar */}
        {listing && (
          <div 
            className="bg-white/10 px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/15 transition-colors"
            onClick={() => navigate(`/woning/${listingId}`)}
          >
            {listing.images?.[0] && (
              <img 
                src={listing.images[0]} 
                alt={listing.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{listing.city}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70 mt-0.5">
                <span className="flex items-center gap-1">
                  <Home className="w-3 h-3" />
                  {listing.propertyType}
                </span>
                <span className="flex items-center gap-1 font-semibold text-white">
                  <Euro className="w-3 h-3" />
                  {listing.price}/mnd
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loadingChat ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
          </div>
        ) : chatMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-[#2E7D32]" />
            </div>
            <h3 className="font-bold text-[#2A2A2A] mb-2">
              {language === 'nl' ? 'Start het gesprek' : 'Start the conversation'}
            </h3>
            <p className="text-[#9E9E9E] text-sm">
              {language === 'nl' 
                ? 'Stuur een bericht naar de verhuurder om het gesprek te starten.'
                : 'Send a message to the landlord to start the conversation.'}
            </p>
          </div>
        ) : (
          <>
            {chatMessages.map((msg, index) => {
              const isOwn = msg.senderId === user?.id;
              const isAIPitch = msg.isAutoPitch || msg.content?.includes('Hallo beste verhuurder');
              
              return (
                <div 
                  key={msg.id || index}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] ${isOwn ? 'order-1' : ''}`}>
                    {isAIPitch && (
                      <div className="bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-2xl rounded-bl-md p-4 text-white mb-1">
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    )}
                    {!isAIPitch && (
                      <div className={`rounded-2xl px-4 py-3 ${
                        isOwn 
                          ? 'bg-[#2E7D32] text-white rounded-br-md' 
                          : 'bg-white text-[#2A2A2A] rounded-bl-md shadow-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    )}
                    <p className={`text-xs text-[#9E9E9E] mt-1 ${isOwn ? 'text-right' : ''}`}>
                      {formatTimestamp(msg.timestamp || msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="sticky bottom-0 bg-white border-t border-[#E5E5E5] p-4 safe-area-bottom">
        {!hasPremium ? (
          /* Premium Paywall */
          <div className="bg-gradient-to-r from-[#1F3A5F]/5 to-[#2E7D32]/5 rounded-2xl p-4 border border-dashed border-[#2E7D32]/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-[#2A2A2A]">
                  {language === 'nl' ? 'Premium vereist' : 'Premium required'}
                </h4>
                <p className="text-xs text-[#9E9E9E]">
                  {language === 'nl' ? 'Om te kunnen chatten' : 'To start chatting'}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/upgrade-match/${listingId}`)}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-full h-12 font-bold"
            >
              <Crown className="w-4 h-4 mr-2" />
              {language === 'nl' ? 'Upgrade naar Premium - €4,99/mnd' : 'Upgrade to Premium - €4.99/mo'}
            </Button>
          </div>
        ) : (
          /* Message Input */
          <div className="flex items-center gap-3">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              placeholder={language === 'nl' ? 'Typ je bericht...' : 'Type your message...'}
              className="flex-1 h-12 rounded-full border-[#E5E5E5] focus:ring-[#2E7D32] bg-[#F7F7F7]"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              className="w-12 h-12 rounded-full bg-[#2E7D32] hover:bg-[#256B2B] p-0"
            >
              {sendingMessage ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
